import React, {useCallback, useState} from 'react';
import {addToast, Button, Dialogue, Modal, Select, TextArea} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {UserFeedbackModelV1} from 'resources/GeneratedModels/UserFeedback/UserFeedbackModel.v1';

interface IssuesModalProps {
  questionId: string;
  handleClose: () => void;
}

const TYPE_OPTIONS = [
  {value: 'typo', text: 'Typo'},
  {value: 'inaccuracy', text: 'Inaccuracy'},
  {value: 'image', text: 'Image'},
  {value: 'formatting', text: 'Formatting'},
  {value: 'suggestion', text: 'Suggestion'},
  {value: 'translation_error', text: 'Translation Error'}
] as const;

const REPORTED_DEVICE_OPTIONS = [
  {value: 'desktop', text: 'Desktop'},
  {value: 'mobile', text: 'Mobile'}
] as const;

export const IssuesModal = ({questionId, handleClose}: IssuesModalProps) => {
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState<(typeof TYPE_OPTIONS)[number]['value'] | ''>('');
  const [reportedDevice, setReportedDevice] = useState<
    (typeof REPORTED_DEVICE_OPTIONS)[number]['value'] | ''
  >('');
  const [comment, setComment] = useState('');

  const submit = useCallback(async () => {
    setSaving(true);
    const feedback = new UserFeedbackModelV1()
      .setComment(comment)
      .setFeedbackType(type)
      .setReportedDevice(reportedDevice)
      .addRelationship({
        type: 'question_v3',
        relation: questionId
      })
      .addRelationship({
        type: 'user_v2',
        relation: sessionStore.getUserId()
      });

    try {
      await feedback.save();
      addToast({
        message: 'Your question feedback has been received.',
        title: 'Success',
        color: 'positive'
      });
    } catch (e) {
      addToast({
        message: 'There was a problem processing your question feedback, please try again.',
        title: 'Error',
        color: 'negative'
      });
    } finally {
      setSaving(false);
      handleClose();
    }
  }, [type, reportedDevice, comment, questionId, handleClose, setSaving]);

  const canSubmit = type && reportedDevice && comment;

  return (
    <Modal ariaLabel='Submit Feedback' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => (
        <Dialogue
          inModal
          size='l'
          className={modalContentStyle}
          handleClose={handleClose}
          title='Submit Question Feedback to Albert.io'
        >
          <Dialogue.Body>
            <div className='u-display_flex u-flex-direction_column u-gap_space-x1'>
              <div className='u-display_flex u-gap_space-x1 u-justify-content_space-between u-align-items_center'>
                <Select value={type} onChange={(e) => setType(e.target.value as any)}>
                  <option value='' disabled>
                    Select Issue Type
                  </option>
                  {TYPE_OPTIONS.map(({value, text}) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </Select>

                <Select
                  value={reportedDevice}
                  onChange={(e) => setReportedDevice(e.target.value as any)}
                >
                  <option value='' disabled>
                    Select Reported Device
                  </option>
                  {REPORTED_DEVICE_OPTIONS.map(({value, text}) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </Select>
              </div>

              <TextArea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{resize: 'vertical'}}
                placeholder='Please provide a detailed description of the issue you have with the question. If this is a technical issue, please email hello@albert.io.'
                rows={3}
              />
            </div>
          </Dialogue.Body>
          <Dialogue.BtnGroup>
            <Button disabled={!canSubmit || saving} color='primary' onClick={submit}>
              Submit
            </Button>
            <CloseButtonWrapper>
              <Button color='secondary' disabled={saving}>
                Close
              </Button>
            </CloseButtonWrapper>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
};
